import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { supabase } from '../supabaseClient';
import { useTranslation } from 'react-i18next';
import { safeRedirectOrDefault } from '@/lib/safeRedirect';

// Story 064 follow-up — verify links in confirmation emails point at our
// own domain instead of <ref>.supabase.co. The email template uses
// {{ .TokenHash }} + this page's URL; we call supabase.auth.verifyOtp
// client-side, which exchanges the hash for a session, signs the user
// in, and then we redirect to `next` (or sensible default).
//
// Handles signup, magiclink, recovery, invite, email_change types
// uniformly — verifyOtp takes the type as a parameter and dispatches
// to the right Supabase Auth flow.

type Phase =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'done'; nextPath: string };

type EmailOtpType =
  | 'signup'
  | 'invite'
  | 'magiclink'
  | 'recovery'
  | 'email_change'
  | 'email';

const VALID_TYPES = new Set<EmailOtpType>([
  'signup',
  'invite',
  'magiclink',
  'recovery',
  'email_change',
  'email',
]);

export function AuthConfirmPage() {
  const { t } = useTranslation('auth');
  const [params] = useSearchParams();
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const navigate = useNavigate();

  useEffect(() => {
    const tokenHash = params.get('token_hash');
    const typeParam = params.get('type');
    const next = params.get('next') || '/';

    if (!tokenHash) {
      setPhase({
        kind: 'error',
        message: t('confirm.errors.missing_token'),
      });
      return;
    }
    if (!typeParam || !VALID_TYPES.has(typeParam as EmailOtpType)) {
      setPhase({
        kind: 'error',
        message: t('confirm.errors.unknown_type', { type: typeParam ?? '(none)' }),
      });
      return;
    }

    let cancelled = false;
    (async () => {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: typeParam as EmailOtpType,
      });
      if (cancelled) return;
      if (error) {
        setPhase({
          kind: 'error',
          // Surface Supabase's message verbatim — it's usually clear
          // ("Email link is invalid or has expired", etc.).
          message: error.message,
        });
        return;
      }
      setPhase({ kind: 'done', nextPath: next });
      // Brief pause so the user sees confirmation, then navigate.
      // react-router's navigate() treats absolute URLs as relative
      // path segments and tacks them onto the current pathname (e.g.
      // /auth/confirm/https:/foo.com/auth/callback). For absolute
      // URLs we reduce same-origin ones to a path; cross-origin URLs
      // are REJECTED — a malicious email template or hijacked
      // recovery flow could otherwise drop the user on evil.com
      // after a successful auth verification.
      setTimeout(() => {
        const isAbsolute = /^https?:\/\//i.test(next);
        if (isAbsolute) {
          try {
            const url = new URL(next);
            if (url.origin === window.location.origin) {
              navigate(url.pathname + url.search + url.hash, { replace: true });
              return;
            }
          } catch {
            /* malformed URL — fall through to a safe default */
          }
          // Cross-origin or malformed absolute URL — refuse the
          // redirect and bounce to home instead of trusting the value.
          navigate('/', { replace: true });
          return;
        }
        // FE-L2: the relative branch must also reject //evil.com.
        navigate(safeRedirectOrDefault(next, '/'), { replace: true });
      }, 1200);
    })();
    return () => {
      cancelled = true;
    };
  }, [params, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md space-y-4 rounded border bg-card p-6 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent/20">
          <Crown className="h-6 w-6 text-accent-foreground" />
        </div>
        {phase.kind === 'loading' && (
          <>
            <h1 className="text-xl font-semibold">{t('confirm.loading_title')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('confirm.loading_body')}
            </p>
          </>
        )}
        {phase.kind === 'done' && (
          <>
            <h1
              data-testid="auth-confirm-done"
              className="text-xl font-semibold"
            >
              {t('confirm.done_title')}
            </h1>
            <p className="text-sm text-muted-foreground">{t('confirm.done_body')}</p>
          </>
        )}
        {phase.kind === 'error' && (
          <>
            <h1 className="text-xl font-semibold">
              {t('confirm.error_title')}
            </h1>
            <p
              data-testid="auth-confirm-error"
              className="text-sm text-destructive"
            >
              {phase.message}
            </p>
            <Button asChild variant="outline">
              <Link to="/login">{t('confirm.back_to_login')}</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
